import { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { DocumentNode } from 'graphql';
import {
  GraphQLClient,
  ClientError,
  RequestDocument,
  Variables,
} from 'graphql-request';
import { RequestInit } from 'graphql-request/dist/types.dom';
import { checkAndGetNewAccessToken } from './checkAndGetNewAccessToken';
import * as Dom from 'graphql-request/dist/types.dom';

export type Document = RequestDocument;
export type RequestHeaders = Dom.RequestInit['headers'];

interface GraphQLBaseQueryResponse {
  meta: {
    request?: ClientError['request'];
    response?: ClientError['response'];
  };
  data?: any;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
}

/**
 * Renew access token if required then make graphql request
 */
export const graphqlRequestBaseQuery = (
  props:
    | { url: string; requestHeaders?: RequestHeaders }
    | { client: GraphQLClient }
): BaseQueryFn<
  {
    document: string | DocumentNode;
    variables?: Variables | void;
    requestHeaders?: RequestInit['headers'];
  },
  unknown,
  Pick<ClientError, 'name' | 'message' | 'stack'>,
  Partial<Pick<ClientError, 'request' | 'response'>>
> => {
  const client =
    'client' in props ? props.client : new GraphQLClient(props.url);

  if ('requestHeaders' in props) {
    client.setHeaders(props.requestHeaders);
  }

  return async ({
    document,
    variables,
    requestHeaders,
  }): Promise<GraphQLBaseQueryResponse> => {
    // if token expired then get new token and set headers
    const { accessToken: newAccessToken } = await checkAndGetNewAccessToken();
    if (newAccessToken) {
      client.setHeader('authorization', newAccessToken);
    }

    /**
     * Make GraphQL request and handle error
     */
    const tryClientRequest = async () => {
      try {
        return {
          data: await client.request(document, variables, requestHeaders),
          meta: {},
        };
      } catch (error) {
        if (error instanceof ClientError) {
          const { name, message, stack, request, response } = error;
          return {
            error: { name, message, stack },
            meta: { request, response },
          };
        }
        throw error;
      }
    };
    return await tryClientRequest();
  };
};
